/**
 * Única porta de injeção de HTML da central de notificações (CENTRAL-04.3).
 *
 * O projeto não tinha sanitizador: há 61 pontos de `dangerouslySetInnerHTML` sem
 * tratamento nenhum, incluindo texto vindo do backend e mensagem digitada por
 * outro jogador. Este utilitário cobre a superfície NOVA; o retrofit dos outros
 * está registrado como risco no design e merece feature própria.
 *
 * PARTE-SE DOS PADRÕES DO DOMPURIFY, de propósito. A primeira versão declarava
 * `ALLOWED_TAGS`/`ALLOWED_ATTR` à mão e os testes mostraram que ela comia a peça:
 * `cellpadding`, `border`, `width`, `align` e `target` sumiam, ou seja, o layout
 * de tabela dos templates ia junto. Medido contra os padrões da biblioteca, tudo
 * isso já é preservado — e `javascript:` no `href` já é removido sem configuração
 * nenhuma. Reescrever a allowlist de uma biblioteca madura é a forma mais fácil
 * de quebrar o conteúdo legítimo achando que se está protegendo.
 *
 * Sobram três ajustes, cada um medido caso a caso — e "medido" aqui significa
 * comparado COM e SEM a opção, não só com ela: a primeira versão deste arquivo
 * afirmava que `FORBID_TAGS` barrava `<style>` sem nunca ter testado o padrão
 * sozinho, e o padrão já barrava.
 */
import DOMPurify from 'dompurify';

/**
 * Elementos de formulário, que o DOMPurify **mantém** por padrão — medido — e que
 * não têm o que fazer num aviso: um `<form>` dentro da peça poderia coletar
 * digitação e postar para fora.
 *
 * `<style>`, `<script>`, `<iframe>`, `<object>` e `<embed>` NÃO estão aqui porque
 * a biblioteca já os remove sozinha, também medido. Listá-los daria a impressão
 * de que esta configuração os barra, e o teste correspondente passaria com ou sem
 * ela — foi exatamente o defeito que a verificação apontou.
 */
const FORBID_TAGS = ['form', 'input', 'button', 'textarea', 'select'];

/** `target` é removido por padrão (proteção contra tabnabbing) e os templates o usam. */
const ADD_ATTR = ['target'];

/**
 * A contrapartida de reabilitar `target`: `rel="noopener noreferrer"` é imposto em
 * toda âncora que abra em outra aba, para a página de destino não alcançar a
 * `window.opener` da sessão. Sem isso, permitir `target` seria trocar o layout
 * fiel por um buraco.
 */
DOMPurify.addHook('afterSanitizeAttributes', node => {
  if (node.tagName === 'A' && node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, { FORBID_TAGS, ADD_ATTR });
}
